<script setup>
import { VForm } from 'vuetify/components/VForm'
import { themeConfig } from '@themeConfig'
import { useTrainerAccountStore } from '@/stores/trainerAccount'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'

definePage({
  meta: {
    layout: 'blank',
    unauthenticatedOnly: true,
  },
})

const accountStore = useTrainerAccountStore()
const router = useRouter()
const { t } = useI18n()

const isPasswordVisible = ref(false)
const formError = ref('')
const successMessage = ref('')
const refVForm = ref()

const form = ref({
  email: '',
  password: '',
  passwordConfirmation: '',
})

const register = async () => {
  formError.value = ''
  successMessage.value = ''
  try {
    const result = await accountStore.register({
      email: form.value.email,
      password: form.value.password,
      passwordConfirmation: form.value.passwordConfirmation,
    })

    successMessage.value = result.message
    await router.push({ name: 'confirm-email', query: { email: form.value.email } })
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message || t('register.genericError')
  }
}

const onSubmit = () => {
  refVForm.value?.validate().then(({ valid }) => {
    if (valid)
      register()
  })
}
</script>

<template>
  <div class="auth-wrapper d-flex align-center justify-center pa-4">
    <VCard
      flat
      border
      max-width="480"
      width="100%"
      class="pa-2"
    >
      <VCardText>
        <RouterLink
          to="/"
          class="d-flex align-center gap-x-3 mb-6 text-decoration-none"
        >
          <VNodeRenderer :nodes="themeConfig.app.logo" />
          <span class="text-h5 text-high-emphasis">FitX</span>
        </RouterLink>

        <h1 class="text-h4 mb-1">
          {{ $t('register.title') }}
        </h1>
        <p class="mb-0 text-medium-emphasis">
          {{ $t('register.subtitle') }}
        </p>
      </VCardText>

      <VCardText>
        <VAlert
          v-if="formError"
          type="error"
          variant="tonal"
          class="mb-4"
          role="alert"
        >
          {{ formError }}
        </VAlert>

        <VAlert
          v-if="successMessage"
          type="success"
          variant="tonal"
          class="mb-4"
          role="status"
        >
          {{ successMessage }}
        </VAlert>

        <VForm
          ref="refVForm"
          @submit.prevent="onSubmit"
        >
          <VRow>
            <VCol cols="12">
              <VTextField
                v-model="form.email"
                :label="$t('common.email')"
                type="email"
                autocomplete="username"
                autofocus
                :rules="[requiredValidator, emailValidator]"
                :disabled="accountStore.loading"
              />
            </VCol>

            <VCol cols="12">
              <VTextField
                v-model="form.password"
                :label="$t('common.password')"
                autocomplete="new-password"
                :rules="[requiredValidator, passwordValidator]"
                :type="isPasswordVisible ? 'text' : 'password'"
                :append-inner-icon="isPasswordVisible ? 'ri-eye-off-line' : 'ri-eye-line'"
                :disabled="accountStore.loading"
                @click:append-inner="isPasswordVisible = !isPasswordVisible"
              />
            </VCol>

            <VCol cols="12">
              <VTextField
                v-model="form.passwordConfirmation"
                :label="$t('common.confirmPassword')"
                autocomplete="new-password"
                :rules="[requiredValidator, confirmedValidator(form.passwordConfirmation, form.password)]"
                :type="isPasswordVisible ? 'text' : 'password'"
                :disabled="accountStore.loading"
              />
            </VCol>

            <VCol cols="12">
              <VBtn
                block
                type="submit"
                :loading="accountStore.loading"
              >
                {{ $t('register.submit') }}
              </VBtn>
            </VCol>

            <VCol
              cols="12"
              class="text-center"
            >
              <span class="text-medium-emphasis">{{ $t('register.alreadyHaveAccount') }}</span>
              <RouterLink
                class="text-primary ms-1"
                :to="{ name: 'login' }"
              >
                {{ $t('register.signIn') }}
              </RouterLink>
            </VCol>
          </VRow>
        </VForm>
      </VCardText>
    </VCard>
  </div>
</template>

<style lang="scss">
@use "@core/scss/template/pages/page-auth.scss";
</style>
